import React, { useState } from 'react';
import { Popconfirm, Space, Switch, Tooltip, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import { usePermissions } from '../../contexts/PermissionContext';
import { setVisibilidadF4 } from '../../api/cxPanelApi';
import { FctEstado } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';

/** Recurso ACL que autoriza el interruptor (el backend lo exige en `POST /encuestas-f4/{id}/visibilidad`). */
export const RECURSO_VISIBILIDAD_F4 = 'Cx.EncuestasF4.Visibilidad.Write';

interface Props {
  id: number;
  visible: boolean;
  quitadaManual?: boolean | null;
  /** Se llama tras guardar con éxito, con el nuevo valor, para que la tabla actualice su fila. */
  onCambiado: (visible: boolean) => void;
}

/**
 * Interruptor «Visible para Mystery» de una encuesta F4, con confirmación. Lo que se quita a mano
 * queda marcado (`BT_QUITADA_MANUAL_CX`) y no se reactiva solo. Sin el permiso de escritura se
 * muestra bloqueado con un candado.
 */
const VisibilidadF4Switch: React.FC<Props> = ({ id, visible, quitadaManual, onCambiado }) => {
  const { hasPermission } = usePermissions();
  const puede = hasPermission(RECURSO_VISIBILIDAD_F4);
  const [guardando, setGuardando] = useState(false);

  const cambiar = async () => {
    setGuardando(true);
    try {
      await setVisibilidadF4(id, !visible);
      onCambiado(!visible);
      message.success(!visible ? 'Encuesta visible para Mystery' : 'Visibilidad para Mystery quitada');
    } catch (err) {
      const status = err instanceof AxiosError ? err.response?.status : undefined;
      message.error(
        status === 403 ? 'No tienes permiso para cambiar la visibilidad de las F4.'
          : status === 404 ? 'La encuesta no existe o no es una F4.'
            : 'No se pudo cambiar la visibilidad. Vuelve a intentarlo.',
      );
    } finally {
      setGuardando(false);
    }
  };

  const interruptor = (
    <Switch
      size="small"
      checked={visible}
      loading={guardando}
      disabled={!puede}
      checkedChildren="Visible"
      unCheckedChildren="Oculta"
    />
  );

  if (!puede) {
    return (
      <Tooltip title={`Necesitas el permiso ${RECURSO_VISIBILIDAD_F4} para cambiarla`}>
        <Space size={4}>{interruptor}<LockOutlined style={{ color: fct.tinta['500'] }} /></Space>
      </Tooltip>
    );
  }

  return (
    <Popconfirm
      title={visible ? 'Quitar visibilidad para Mystery' : 'Hacer visible para Mystery'}
      description={visible
        ? 'Mystery dejará de ver esta encuesta. Lo que se quita a mano no se reactiva solo.'
        : 'Mystery podrá ver y gestionar esta encuesta en su portal.'}
      okText={visible ? 'Quitar' : 'Hacer visible'}
      cancelText="Cancelar"
      okButtonProps={{ danger: visible }}
      disabled={guardando}
      onConfirm={cambiar}
    >
      {/* El Switch no cambia por sí mismo: es controlado y solo cambia tras confirmar. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {interruptor}
        {quitadaManual && !visible && (
          <Tooltip title="CxCp la ocultó a mano: el hito R1F4 ya no la hará visible sola. Para que Mystery la vea, actívala aquí.">
            <span><FctEstado personalizado={{ etiqueta: 'Ocultada a mano', familia: 'neutro' }} /></span>
          </Tooltip>
        )}
      </span>
    </Popconfirm>
  );
};

export default VisibilidadF4Switch;
